// Eskom PARCC — Manage Transactions (admin).
//
// The monolith's Manage screen (`renderManage` / `renderManageDetail` /
// `saveManagedTransaction`, git 6810c83:index.html ~2347), rebuilt: a
// filterable/sortable list of ALL transactions on the left and a base-fields
// editor on the right. An admin selects a row to EDIT its writable base fields,
// or chooses "Add transaction" for a blank ADD form. Both write modes go through
// the shared `useUpsertTransaction` (Add inserts a new ref; Edit updates an
// existing one, guarded by `checkRowVersion`).
//
// Admin-only: the route is gated by <RequireRole role="admin"> AND the editor
// re-confirms the admin role before any write (defence-in-depth). Reads are the
// whole population (the register's `useTransactions`), filtered/sorted/selected
// entirely client-side — no compliance maths here (Manage edits base fields, not
// findings), so rows carry an empty compliance and that filter is a no-op.
//
// Soft-delete: the editor's Delete action (admin-only, edit mode) flips the
// transaction's `deleted` flag via SoftDeleteTransaction, then invalidates so the
// row drops out of the register. It records a best-effort AuditLog entry.
import { useEffect, useMemo, useState } from 'react';
import {
  DataTable,
  Button,
  Badge,
  complianceClass,
  LoadingState,
  ErrorState,
  EmptyState,
  type Column,
  Pager,
  usePagination,
} from '../../components/ui';
import { useManageTransactions } from '../../hooks';
import { useAuth } from '../../lib/auth';
import { formatRand } from '../../lib/money';
import { unionOptions } from '../../lib/options';
import { DATASET_OPTIONS, DIVISION_OPTIONS } from '../../domain/enums';
import { canonicalDivision } from '../../domain/divisions';
import {
  filterTransactions,
  distinctValues,
  EMPTY_TRANSACTION_FILTERS,
  type TransactionFilters as Filters,
  type TransactionRow,
} from '../transactions/filterTransactions';
import { TransactionFilters } from '../transactions/TransactionFilters';
import { ManageEditor } from './ManageEditor';

export function ManagePage(): React.JSX.Element {
  const { role } = useAuth();
  const transactionsQuery = useManageTransactions();
  const [filters, setFilters] = useState<Filters>(EMPTY_TRANSACTION_FILTERS);
  const [selectedRef, setSelectedRef] = useState<string | null>(null);
  // The active editor: 'add' (blank), 'edit' (the selected row), or null (none).
  const [editorMode, setEditorMode] = useState<'add' | 'edit' | null>(null);

  const isAdmin = role === 'admin';

  // The whole population INCLUDING soft-deleted (removed) rows — the manage read
  // (useManageTransactions) carries them so the admin can see and restore removed
  // references, and the add-mode uniqueness pre-check considers them (the DB unique
  // constraint on referenceNo covers deleted rows too).
  const transactions = useMemo(() => transactionsQuery.data ?? [], [transactionsQuery.data]);
  const removedCount = useMemo(() => transactions.filter((t) => t.deleted).length, [transactions]);

  // Manage edits base fields, not findings, so it computes no effective
  // compliance — every row carries an empty compliance. The shared
  // `filterTransactions` then treats the compliance filter as a no-op (only its
  // "All" sentinel is offered below).
  const rows: TransactionRow[] = useMemo(
    () => transactions.map((tx) => ({ tx, compliance: '' })),
    [transactions],
  );

  const datasetOptions = useMemo(
    () =>
      unionOptions(
        DATASET_OPTIONS,
        distinctValues(rows, (t) => t.dataset),
      ),
    [rows],
  );
  const divisionOptions = useMemo(
    () =>
      unionOptions(
        DIVISION_OPTIONS,
        // Canonical so raw spelling variants collapse to one option, matching the
        // canonical filter in filterTransactions (owner feedback Aug 2026).
        distinctValues(rows, (t) => canonicalDivision(t.division)),
      ),
    [rows],
  );
  const yearOptions = useMemo(() => distinctValues(rows, (t) => t.year), [rows]);
  const periodOptions = useMemo(() => distinctValues(rows, (t) => t.period), [rows]);

  const filtered = useMemo(() => filterTransactions(rows, filters), [rows, filters]);
  // Bound the list — both themes get a pager (Lumen 7–8/page, Legacy 50).
  const pagination = usePagination(filtered);

  const selectedTx = useMemo(
    () => transactions.find((t) => t.referenceNo === selectedRef) ?? null,
    [transactions, selectedRef],
  );

  // If the selected transaction is filtered out (or removed), drop the edit
  // editor so it never lingers over a row the admin can no longer see.
  const selectedVisible = useMemo(
    () => (selectedRef ? filtered.some((r) => r.tx.referenceNo === selectedRef) : false),
    [filtered, selectedRef],
  );
  useEffect(() => {
    if (editorMode === 'edit' && !selectedVisible) setEditorMode(null);
  }, [editorMode, selectedVisible]);

  const columns = useMemo<Column<TransactionRow>[]>(
    () => [
      {
        key: 'referenceNo',
        header: 'Reference No',
        sortable: true,
        accessor: (r) => r.tx.referenceNo,
        render: (r) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {r.tx.referenceNo}
            {r.tx.deleted && (
              <Badge
                className={complianceClass('Non-Compliance: Possible IE')}
                title="Removed from the register — click to restore"
              >
                Removed
              </Badge>
            )}
          </span>
        ),
      },
      {
        key: 'dataset',
        header: 'Dataset',
        sortable: true,
        accessor: (r) => r.tx.dataset,
        render: (r) => r.tx.dataset,
      },
      {
        key: 'division',
        header: 'Division',
        sortable: true,
        accessor: (r) => r.tx.division ?? '',
        render: (r) => r.tx.division ?? '—',
      },
      {
        key: 'supplier',
        header: 'Supplier',
        sortable: true,
        accessor: (r) => r.tx.supplier ?? '',
        render: (r) => r.tx.supplier ?? '—',
      },
      {
        key: 'value',
        header: 'Value',
        sortable: true,
        align: 'right',
        accessor: (r) => r.tx.value,
        render: (r) => formatRand(r.tx.value),
      },
      {
        key: 'mechanism',
        header: 'Mechanism',
        sortable: true,
        accessor: (r) => r.tx.mechanism ?? '',
        render: (r) => r.tx.mechanism ?? '—',
      },
    ],
    [],
  );

  if (transactionsQuery.isLoading) return <LoadingState message="Loading transactions…" />;
  if (transactionsQuery.error) {
    const err = transactionsQuery.error;
    return (
      <ErrorState
        message="Could not load the transactions for management."
        detail={err instanceof Error ? err.message : String(err)}
        onRetry={() => {
          void transactionsQuery.refetch();
        }}
      />
    );
  }

  return (
    <>
      <TransactionFilters
        filters={filters}
        onChange={(patch) => setFilters((f) => ({ ...f, ...patch }))}
        onReset={() => setFilters(EMPTY_TRANSACTION_FILTERS)}
        datasetOptions={datasetOptions}
        yearOptions={yearOptions}
        divisionOptions={divisionOptions}
        periodOptions={periodOptions}
        complianceOptions={[]}
      />

      <div className="split">
        <div className="card">
          <div className="section-head">
            <div>
              <h3>Manage Transactions</h3>
              <div className="small">
                {filtered.length === rows.length
                  ? `${rows.length.toLocaleString('en-ZA')} transaction(s)`
                  : `${filtered.length.toLocaleString('en-ZA')} of ${rows.length.toLocaleString('en-ZA')} transaction(s)`}
                {removedCount > 0 && ` · ${removedCount.toLocaleString('en-ZA')} removed`}
              </div>
            </div>
            <div className="actions">
              <Button
                variant="primary"
                onClick={() => {
                  setSelectedRef(null);
                  setEditorMode('add');
                }}
              >
                Add transaction
              </Button>
            </div>
          </div>
          {rows.length === 0 ? (
            <EmptyState message="No transactions captured yet. Use “Add transaction” to create the first." />
          ) : (
            <DataTable
              columns={columns}
              rows={pagination.slice}
              getRowKey={(r) => r.tx.referenceNo || r.tx.id}
              onRowClick={(r) => {
                setSelectedRef(r.tx.referenceNo);
                setEditorMode('edit');
              }}
              initialSort={{ key: 'referenceNo', dir: 'asc' }}
              selectedRowKey={selectedRef ?? undefined}
              emptyMessage="No transactions match the filters."
            />
          )}
          {rows.length > 0 && <Pager pagination={pagination} noun="transaction" />}
        </div>

        {editorMode === 'add' ? (
          <ManageEditor
            mode="add"
            transactions={transactions}
            isAdmin={isAdmin}
            onClose={() => setEditorMode(null)}
          />
        ) : editorMode === 'edit' && selectedTx ? (
          <ManageEditor
            key={selectedTx.referenceNo || selectedTx.id}
            mode="edit"
            tx={selectedTx}
            transactions={transactions}
            isAdmin={isAdmin}
            onClose={() => setEditorMode(null)}
          />
        ) : (
          <EmptyState message="Select a transaction to edit its base fields, or add a new one." />
        )}
      </div>
    </>
  );
}
